import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider, useAppContext } from './contexts/AppContext';
import { hasSocioPermission, canAccessReporteMedicos, hasSubmoduleAccess } from './lib/permissions';
import Global502Modal from './components/Global502Modal';
import MainLayout from './components/MainLayout';
import Login from './pages/Login';
import Home from './pages/Home';

// Rutas móviles / públicas / quioscos (aisladas para carga inmediata en dispositivos móviles y de bajo consumo)
const MobileUpload = lazy(() => import('./pages/MobileUpload'));
const IpadConsentimiento = lazy(() => import('./pages/IpadConsentimiento'));
const TurnosTV = lazy(() => import('./pages/TurnosTV'));
const PatientRegistration = lazy(() => import('./pages/PatientRegistration'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const ReviewFeedback = lazy(() => import('./pages/ReviewFeedback'));
const ValidarDocumentoPublico = lazy(() => import('./pages/ValidarDocumentoPublico'));
const SetPassword = lazy(() => import('./pages/SetPassword'));
const PublicAgendarCita = lazy(() => import('./pages/PublicAgendarCita'));
const LandingTamizCaderaLosCabos = lazy(() => import('./pages/LandingTamizCaderaLosCabos'));
const RegistroTamizCaderaLosCabos = lazy(() => import('./pages/RegistroTamizCaderaLosCabos'));
const ReconfirmacionTamizCaderaLosCabos = lazy(() => import('./pages/ReconfirmacionTamizCaderaLosCabos'));
const PublicUnsubscribe = lazy(() => import('./pages/PublicUnsubscribe'));

import { apiFetch } from './lib/apiClient';
import {
  fetchAllYearsEventsCached,
  fetchRangeEventsCached,
  fetchYearEventsCached,
  mergeEventsForSelectedYears,
  resolveInitialMonthlyFetchWindows,
  resolveInitialWeeklyFetchWindow
} from './lib/monthlyProjections';

// Módulos ERP administrativos (cargados bajo demanda)
const loadRecepcionPacientes = () => import('./pages/RecepcionPacientes');
const RecepcionPacientes = lazy(loadRecepcionPacientes);

if (typeof window !== 'undefined' && window.location?.pathname?.startsWith('/finanzas/recepcion')) {
  loadRecepcionPacientes().catch(() => {});
  try {
    const initParams = new URLSearchParams(window.location.search || '');
    const initTab = initParams.get('tab');
    if (initTab === 'multianual') {
      import('./components/VistaMultianual').catch(() => {});
      const eventsPromise = fetchAllYearsEventsCached(apiFetch);
      Promise.all([eventsPromise, import('./lib/multianualAnalytics')])
        .then(([data, mod]) => {
          if (Array.isArray(data) && data.length > 0 && typeof mod?.computeMultianualIntelligence === 'function') {
            mod.computeMultianualIntelligence(data, ['ALL'], ['ALL']);
          }
        })
        .catch(() => {});
    } else if (initTab === 'anual') {
      import('./components/VistaAnual').catch(() => {});
      const rawYearsParam = initParams.get('years') || initParams.get('year') || '';
      const parsedYears = Array.from(
        new Set(
          rawYearsParam
            .split(',')
            .map((s) => s.trim())
            .filter((s) => /^\d{4}$/.test(s))
            .filter((s) => {
              const yNum = parseInt(s, 10);
              return yNum >= 2021 && yNum <= 2035;
            })
        )
      ).sort((a, b) => Number(b) - Number(a));
      const defaultYr = String(Math.max(new Date().getFullYear(), 2026));
      const targetYears = parsedYears.length > 0 ? parsedYears : [defaultYr];
      const yearsPromise = Promise.all(targetYears.map((yr) => fetchYearEventsCached(yr, apiFetch)));
      Promise.all([yearsPromise, import('./lib/anualAnalytics')])
        .then(([yearResults, mod]) => {
          const allLoaded =
            Array.isArray(yearResults) &&
            yearResults.length === targetYears.length &&
            yearResults.every((arr) => Array.isArray(arr) && arr.length > 0);
          if (allLoaded && typeof mod?.computeAnualIntelligence === 'function') {
            const merged = mergeEventsForSelectedYears(targetYears);
            if (Array.isArray(merged) && merged.length > 0) {
              mod.computeAnualIntelligence(merged, ['ALL'], ['ALL'], targetYears);
            }
          }
        })
        .catch(() => {});
    } else if (initTab === 'mensual') {
      import('./components/VistaMensual').catch(() => {});
      const { start_date, end_date, hist_start, hist_end } = resolveInitialMonthlyFetchWindows(window.location.search || '');
      if (start_date && end_date) {
        fetchRangeEventsCached(start_date, end_date, apiFetch).catch(() => {});
      }
      if (hist_start && hist_end) {
        fetchRangeEventsCached(hist_start, hist_end, apiFetch).catch(() => {});
      }
    } else if (initTab === 'semanal') {
      import('./components/VistaSemanal').catch(() => {});
      const { fetchStart, fetchEnd } = resolveInitialWeeklyFetchWindow(window.location.search || '');
      if (fetchStart && fetchEnd) {
        fetchRangeEventsCached(fetchStart, fetchEnd, apiFetch).catch(() => {});
      }
    } else if (initTab === 'corte') {
      import('./components/CorteDelDia').catch(() => {});
    } else if (initTab === 'vistacalendario') {
      import('./components/DailyCalendarTab').catch(() => {});
    } else if (initTab === 'kanban') {
      import('./components/KanbanView').catch(() => {});
    } else if (initTab === 'graficas') {
      import('./components/GraficasView').catch(() => {});
    } else if (!initTab || initTab === 'recepcion') {
      import('./components/CalendarView').catch(() => {});
      import('./components/PatientSearchModal').catch(() => {});
      import('./components/PatientForm').catch(() => {});
    }
  } catch {
    // ignore URL parse errors in non-standard environments
  }
}
const Agendamiento = lazy(() => import('./pages/Agendamiento'));
const FinanzasCorporativas = lazy(() => import('./pages/FinanzasCorporativas'));
const Operaciones = lazy(() => import('./pages/Operaciones'));
const CRMMedicos = lazy(() => import('./pages/CRMMedicos'));
const ContactosMedicos = lazy(() => import('./pages/medicos/Contactos'));
const CRMPacientes = lazy(() => import('./pages/CRMPacientes'));
const MarketingMasivo = lazy(() => import('./pages/MarketingMasivo'));
const RecursosHumanos = lazy(() => import('./pages/RecursosHumanos'));
const EmpleadosManager = lazy(() => import('./pages/EmpleadosManager'));
const EmpleadoProfile = lazy(() => import('./pages/EmpleadoProfile'));
const Checador = lazy(() => import('./pages/Checador'));
const UsuariosExternos = lazy(() => import('./pages/hr/UsuariosExternos'));
const TicketsManager = lazy(() => import('./pages/TicketsManager'));
const AffiliateAgendamiento = lazy(() => import('./pages/affiliate/AffiliateAgendamiento'));
const AffiliateFacturacion = lazy(() => import('./pages/affiliate/AffiliateFacturacion'));
const AffiliateConfiguracion = lazy(() => import('./pages/affiliate/AffiliateConfiguracion'));
const ConfiguracionGlobal = lazy(() => import('./pages/ConfiguracionGlobal'));
const ValidadorDocumentos = lazy(() => import('./pages/ValidadorDocumentos'));
const MiPerfil = lazy(() => import('./pages/MiPerfil'));
const DeveloperTools = lazy(() => import('./pages/DeveloperTools'));
const BatchImport = lazy(() => import('./pages/BatchImport'));
const Simulator = lazy(() => import('./pages/Simulator'));
const SystemCheck = lazy(() => import('./pages/SystemCheck'));
const JSONImport = lazy(() => import('./pages/JSONImport'));
const RadiologoPortal = lazy(() => import('./pages/radiologo/RadiologoPortal'));


// Actualiza el título del documento según la ruta y parámetros activos
function TitleManager() {
  const location = useLocation();
  const { moduleTitle, activeTab, subOptions, activeItemTitle } = useAppContext();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const pathname = location.pathname;

    let moduleName;
    let subModuleName;

    if (pathname === '/login') {
      moduleName = 'Iniciar Sesión';
    } else if (pathname === '/') {
      moduleName = 'Portal Principal';
    } else if (pathname.startsWith('/affiliate')) {
      moduleName = 'Affiliate Marketing Platform';
      if (pathname.includes('/configuracion')) {
        subModuleName = 'Configuración';
      } else if (pathname.includes('/facturacion')) {
        subModuleName = 'Facturación';
      } else {
        subModuleName = 'Agendamiento';
      }
    } else if (pathname.startsWith('/radiologo')) {
      moduleName = 'Portal Médico Radiólogo';
      subModuleName = 'Agenda Coronaria y Visor Clínico';
    } else if (pathname.startsWith('/tickets')) {
      moduleName = 'Tickets Internos';
      subModuleName = 'Gestión de Tareas';
    } else if (pathname.startsWith('/agendamiento')) {
      moduleName = 'Agendamiento y Atención al Paciente';
      const tabMap = {
        'manual': 'Agendamiento Manual',
        'atencion': 'Atención al Paciente',
        'agenda': 'Agenda de Citas',
        'simulador': 'Simulador de Análisis',
        'reporte': 'Reporte Semanal',
        'analisis': 'Análisis de Llamadas',
        'bi': 'Análisis de Llamadas',
        'leads': 'Leads Kommo'
      };
      subModuleName = tabMap[tabParam] || (tabParam ? tabParam.charAt(0).toUpperCase() + tabParam.slice(1) : 'Atención al Paciente');
    } else if (pathname.startsWith('/finanzas/recepcion')) {
      moduleName = 'Recepción';
      const tabMap = {
        'recepcion': 'Vista Lista',
        'graficas': 'Gráficas',
        'vistacalendario': 'Vista Calendario',
        'kanban': 'Vista Kanban',
        'corte': 'Corte del Día',
        'revision_medicos': 'Revisión Médicos',
        'reporte_medicos': 'Reporte por Médicos',
        'semanal': 'Vista Semanal',
        'mensual': 'Vista Mensual',
        'anual': 'Vista Anual',
        'multianual': 'Vista Multianual'
      };
      subModuleName = tabMap[tabParam] || 'Vista Lista';
    } else if (pathname.startsWith('/finanzas/corporativas')) {
      moduleName = 'Finanzas Corp.';
      const tabMap = {
        'estados_cuenta': 'Estados de Cuenta',
        'ingresos': 'Ingresos Corporativos',
        'gastos': 'Gastos Corporativos',
        'flujo_caja': 'Flujo de Caja',
        'cuentas_pagar': 'Cuentas por Pagar',
        'cuentas_cobrar': 'Cuentas por Cobrar',
        'proveedores': 'Proveedores',
        'prestamos': 'Préstamos a Devolver',
        'estados_financieros': 'Estados Financieros'
      };
      subModuleName = tabMap[tabParam] || 'Estados de Cuenta';
    } else if (pathname.startsWith('/operaciones')) {
      moduleName = 'Operaciones Médicas';
      const opMap = {
        'agenda': 'Agenda Técnica',
        'verificacion': 'Verificación Técnica',
        'salas': 'Sala de Estudios'
      };
      subModuleName = opMap[tabParam] || (pathname.includes('/agenda') ? 'Agenda Técnica' : (pathname.includes('/verificacion') ? 'Verificación Técnica' : (pathname.includes('/salas') ? 'Sala de Estudios' : 'Agenda Técnica')));
    } else if (pathname.startsWith('/sala-estudios')) {
      moduleName = 'Operaciones Médicas';
      subModuleName = 'Sala de Estudios';
    } else if (pathname === '/hr') {
      moduleName = 'Recursos Humanos';
      subModuleName = 'Control de Asistencia';
    } else if (pathname === '/hr/externos') {
      moduleName = 'Recursos Humanos';
      subModuleName = 'Usuarios Externos';
    } else if (pathname.startsWith('/hr/empleados')) {
      moduleName = 'Recursos Humanos';
      subModuleName = 'Directorio de Empleados';
    } else if (pathname.startsWith('/checador')) {
      moduleName = 'Recursos Humanos';
      subModuleName = 'Checador de Asistencia';
    } else if (pathname.startsWith('/crm-pacientes')) {
      moduleName = 'CRM';
      subModuleName = 'Pacientes';
    } else if (pathname.startsWith('/crm')) {
      moduleName = 'CRM';
      subModuleName = 'Médicos';
    } else if (pathname.startsWith('/marketing')) {
      moduleName = 'Marketing';
      subModuleName = 'Envíos Masivos';
    } else if (pathname.startsWith('/configuracion')) {
      moduleName = 'Configuración Global';
      subModuleName = 'Ajustes del Sistema';
    } else if (pathname.startsWith('/validador')) {
      moduleName = 'Validador de Documentos';
      subModuleName = 'Certificación Digital';
    } else if (pathname.startsWith('/validar') || pathname.startsWith('/v/')) {
      moduleName = 'Verificación de Documento';
      subModuleName = 'Autenticidad Oficial';
    } else if (pathname.startsWith('/system/processes')) {
      moduleName = 'System Check';
      const secParam = searchParams.get('sec');
      subModuleName = secParam === 'health' ? 'Estado del Sistema (Live)' : 'Diagnóstico de Procesos';
    } else if (pathname.startsWith('/developer')) {
      moduleName = 'Desarrollador';
      subModuleName = 'Diagnóstico';
    } else if (pathname.startsWith('/dev/simulador')) {
      moduleName = 'Desarrollador';
      subModuleName = 'Simulador';
    } else if (pathname.startsWith('/tv/turnos')) {
      moduleName = 'Pantalla TV';
      subModuleName = 'Turnos';
    } else if (pathname.startsWith('/registro')) {
      moduleName = 'Registro de Pacientes';
    } else if (pathname.startsWith('/sesion-tamiz-de-cadera-los-cabos')) {
      document.title = 'Tamiz de Cadera, por la Dra. Madelinne Granados, IMAGINEM Radiologia';
      return;
    } else if (pathname.startsWith('/agendar-cita-tamiz') || pathname.startsWith('/agendar-cita-cadera')) {
      document.title = 'Tamiz de Cadera Pediátrico (Método de Graf) | Imaginem Radiología';
      return;
    } else if (
      pathname.startsWith('/cancelar-suscripcion') ||
      pathname.startsWith('/desuscribir') ||
      pathname.startsWith('/unsubscribe') ||
      pathname.startsWith('/baja-boletin')
    ) {
      document.title = 'Cancelar suscripción | Imaginem Radiología';
      return;
    } else {
      moduleName = moduleTitle || 'Módulo';
      if (activeTab) {
        const foundSub = subOptions?.find(s => s.id === activeTab);
        subModuleName = foundSub ? foundSub.label : (activeTab.charAt(0).toUpperCase() + activeTab.slice(1));
      }
    }

    // Reordenado de abajo hacia arriba:
    // [Detalle/Cita #ID - Nombre] - [Submódulo] - [Módulo] - GEMA ERP - Imaginem Radiología
    const parts = [];
    if (activeItemTitle) parts.push(activeItemTitle);
    if (subModuleName && subModuleName !== moduleName) parts.push(subModuleName);
    if (moduleName) parts.push(moduleName);
    parts.push('GEMA ERP');
    parts.push('Imaginem Radiología');

    document.title = parts.join(' - ');
  }, [location.pathname, location.search, moduleTitle, activeTab, subOptions, activeItemTitle]);

  return null;
}

// Protege rutas para que requieran autenticación
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) return <div className="flex h-screen items-center justify-center font-bold text-indigo-600">Cargando...</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  
  if (location.pathname.startsWith('/finanzas/recepcion')) {
    const sp = new URLSearchParams(location.search);
    const targetTab = sp.get('tab') || 'recepcion';
    const eventId = sp.get('eventId');
    if ((targetTab === 'recepcion' || eventId) && !hasSubmoduleAccess(user, 'recepcion', 'recepcion')) {
      const redirectParams = new URLSearchParams();
      redirectParams.set('tab', 'manual');
      if (sp.get('date')) redirectParams.set('date', sp.get('date'));
      if (eventId) redirectParams.set('eventId', eventId);
      return <Navigate to={`/agendamiento?${redirectParams.toString()}`} replace />;
    }
  }

  if (allowedRoles) {
    const userRoles = Array.isArray(user?.roles) ? user.roles : [];
    const hasRole = userRoles.includes('admin') || hasSocioPermission(user) || (allowedRoles.includes('recepcion') && canAccessReporteMedicos(user)) || allowedRoles.some(r => {
      if (userRoles.includes(r)) return true;
      const subPrefix = `${r}.`;
      return userRoles.some(userRole => typeof userRole === 'string' && userRole.startsWith(subPrefix));
    });
    if (!hasRole) return <div className="flex h-screen items-center justify-center text-red-500 font-bold">Acceso Denegado</div>;
  }
  
  return <MainLayout>{children}</MainLayout>;
};

// Redirección por legado de /sala-estudios a submódulo de Operaciones Médicas
function SalaEstudiosLegacyRedirect() {
  const { modalidad } = useParams();
  const target = modalidad ? `/operaciones?tab=salas&modalidad=${encodeURIComponent(modalidad)}` : '/operaciones?tab=salas';
  return <Navigate to={target} replace />;
}

function MobileLoadingFallback() {
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6 text-center text-white font-sans">
      <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-4" />
      <h1 className="text-base font-bold text-white mb-1">Cargando Escáner Imaginem...</h1>
      <p className="text-xs text-slate-400">Optimizando para tu dispositivo</p>
    </div>
  );
}

function DefaultLoadingFallback() {
  return (
    <div className="flex h-screen items-center justify-center font-bold text-indigo-600 bg-slate-50">
      Cargando...
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppProvider>
          <TitleManager />
          <Global502Modal />
          <Suspense fallback={<DefaultLoadingFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/mobile-upload" element={
                <Suspense fallback={<MobileLoadingFallback />}>
                  <MobileUpload />
                </Suspense>
              } />
              <Route path="/ipad-consentimiento" element={<IpadConsentimiento />} />
          
          <Route path="/" element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          } />

          <Route path="/registro" element={<PatientRegistration />} />
          <Route path="/tv/turnos" element={<TurnosTV />} />
          <Route path="/perfil" element={
            <ProtectedRoute>
              <MiPerfil />
            </ProtectedRoute>
          } />

          <Route path="/valora/feedback" element={<ReviewFeedback />} />
          <Route path="/valora" element={<ReviewPage />} />

          {/* Validador Público (Escaneo QR) */}
          <Route path="/validar/:folio" element={<ValidarDocumentoPublico />} />
          <Route path="/v/:folio" element={<ValidarDocumentoPublico />} />

          {/* Auto-agendamiento Público para Pacientes */}
          <Route path="/agendar-cita" element={<PublicAgendarCita mode="general" />} />
          <Route path="/agendar-cita-calcio-score" element={<PublicAgendarCita mode="calcio-score" />} />
          <Route path="/agendar-cita-score-calcio" element={<PublicAgendarCita mode="calcio-score" />} />
          <Route path="/agendar-cita-calcio-score-2" element={<PublicAgendarCita mode="calcio-score-2" />} />
          <Route path="/agendar-cita-score-calcio-2" element={<PublicAgendarCita mode="calcio-score-2" />} />
          <Route path="/agendar-cita-mes-rosa" element={<PublicAgendarCita mode="mes-rosa" />} />
          <Route path="/agendar-cita-tamiz-cadera" element={<PublicAgendarCita mode="tamiz-cadera" />} />
          <Route path="/agendar-cita-tamiz-de-cadera" element={<PublicAgendarCita mode="tamiz-cadera" />} />
          <Route path="/agendar-cita-cadera" element={<PublicAgendarCita mode="tamiz-cadera" />} />

          {/* Cancelación pública de suscripción / Newsletter */}
          <Route path="/cancelar-suscripcion" element={<PublicUnsubscribe />} />
          <Route path="/cancelar-suscripcion/confirmar" element={<PublicUnsubscribe />} />
          <Route path="/desuscribir" element={<PublicUnsubscribe />} />
          <Route path="/unsubscribe" element={<PublicUnsubscribe />} />
          <Route path="/baja-boletin" element={<PublicUnsubscribe />} />

          {/* Landing Promocional: Sesión Tamiz de Cadera Los Cabos (Dra. Madelinne Granados) */}
          <Route path="/sesion-tamiz-de-cadera-los-cabos-septiembre-2026" element={<LandingTamizCaderaLosCabos />} />
          <Route path="/sesion-tamiz-de-cadera-los-cabos-septiembre-2026/registro" element={<RegistroTamizCaderaLosCabos />} />
          <Route path="/sesion-tamiz-de-cadera-los-cabos-octubre-2026" element={<LandingTamizCaderaLosCabos />} />
          <Route path="/sesion-tamiz-de-cadera-los-cabos-octubre-2026/registro" element={<RegistroTamizCaderaLosCabos />} />
          <Route path="/sesion-tamiz-de-cadera-los-cabos" element={<LandingTamizCaderaLosCabos />} />
          <Route path="/sesion-tamiz-de-cadera-los-cabos/registro" element={<RegistroTamizCaderaLosCabos />} />
          <Route path="/sesion-tamiz-de-cadera-los-cabos/reconfirmar" element={<ReconfirmacionTamizCaderaLosCabos />} />
          <Route path="/sesion-tamiz-de-cadera-los-cabos-septiembre-2026/reconfirmar" element={<ReconfirmacionTamizCaderaLosCabos />} />
          <Route path="/sesion-tamiz-de-cadera-los-cabos-octubre-2026/reconfirmar" element={<ReconfirmacionTamizCaderaLosCabos />} />
          <Route path="/reconfirmar-tamiz-cadera" element={<ReconfirmacionTamizCaderaLosCabos />} />

          {/* Validador de Documentos ERP */}
          <Route path="/validador" element={
            <ProtectedRoute allowedRoles={['admin', 'medico', 'recepcion', 'patient_care', 'hr', 'dev']}>
              <ValidadorDocumentos />
            </ProtectedRoute>
          } />

          {/* Sala de Estudios (Rutas heredadas - redirigen a Operaciones Médicas) */}
          <Route path="/sala-estudios" element={<Navigate to="/operaciones?tab=salas" replace />} />
          <Route path="/sala-estudios/:modalidad" element={<SalaEstudiosLegacyRedirect />} />

          {/* Finanzas Module */}
          <Route path="/finanzas/recepcion" element={
            <ProtectedRoute allowedRoles={['admin', 'recepcion', 'patient_care', 'operaciones']}>
              <RecepcionPacientes />
            </ProtectedRoute>
          } />
          <Route path="/finanzas/corporativas" element={
            <ProtectedRoute allowedRoles={['admin', 'finance']}>
              <FinanzasCorporativas />
            </ProtectedRoute>
          } />

          {/* HR Module */}
          <Route path="/hr" element={
            <ProtectedRoute allowedRoles={['admin', 'hr', 'medico']}>
              <RecursosHumanos />
            </ProtectedRoute>
          } />
          <Route path="/hr/empleados" element={
            <ProtectedRoute allowedRoles={['admin', 'hr']}>
              <EmpleadosManager />
            </ProtectedRoute>
          } />
          <Route path="/hr/empleados/:id" element={
            <ProtectedRoute allowedRoles={['admin', 'hr']}>
              <EmpleadoProfile />
            </ProtectedRoute>
          } />
          <Route path="/hr/externos" element={
            <ProtectedRoute allowedRoles={['admin', 'hr']}>
              <UsuariosExternos />
            </ProtectedRoute>
          } />
          <Route path="/import-json" element={
            <ProtectedRoute allowedRoles={['admin', 'dev']}>
              <JSONImport />
            </ProtectedRoute>
          } />
          <Route path="/checador" element={
            <ProtectedRoute>
              <Checador />
            </ProtectedRoute>
          } />



          {/* Operaciones Médicas y Submódulos */}
          <Route path="/operaciones" element={
            <ProtectedRoute allowedRoles={['admin', 'operaciones', 'patient_care', 'tecnico', 'medico', 'dev']}>
              <Operaciones />
            </ProtectedRoute>
          } />
          <Route path="/operaciones/:tab" element={
            <ProtectedRoute allowedRoles={['admin', 'operaciones', 'patient_care', 'tecnico', 'medico', 'dev']}>
              <Operaciones />
            </ProtectedRoute>
          } />
          
          {/* CRM Médicos / Contactos */}
          <Route path="/erp/medicos/contactos" element={
            <ProtectedRoute allowedRoles={['admin', 'medico', 'patient_care', 'operaciones', 'dev']}>
              <ContactosMedicos />
            </ProtectedRoute>
          } />

          {/* Agendamiento Module */}
          <Route path="/agendamiento" element={
            <ProtectedRoute allowedRoles={['admin', 'agendamiento', 'patient_care']}>
              <Agendamiento />
            </ProtectedRoute>
          } />

          {/* CRM Module */}
          <Route path="/crm" element={
            <ProtectedRoute allowedRoles={['admin', 'crm', 'canvaseador', 'closer']}>
              <CRMMedicos />
            </ProtectedRoute>
          } />
          
          <Route path="/crm-pacientes" element={
            <ProtectedRoute allowedRoles={['admin', 'crm', 'patient_care']}>
              <CRMPacientes />
            </ProtectedRoute>
          } />

          {/* Marketing Module */}
          <Route path="/marketing" element={
            <ProtectedRoute allowedRoles={['admin', 'marketing']}>
              <MarketingMasivo />
            </ProtectedRoute>
          } />

          {/* Developer Tools */}
          <Route path="/developer" element={
            <ProtectedRoute allowedRoles={['admin', 'dev']}>
              <DeveloperTools />
            </ProtectedRoute>
          } />
          
          <Route path="/dev/importador" element={
            <ProtectedRoute allowedRoles={['admin', 'dev']}>
              <BatchImport />
            </ProtectedRoute>
          } />
          
          <Route path="/dev/simulador" element={
            <ProtectedRoute allowedRoles={['admin', 'dev']}>
              <Simulator />
            </ProtectedRoute>
          } />
          
          <Route path="/system/processes" element={
            <ProtectedRoute allowedRoles={['admin', 'dev']}>
              <SystemCheck />
            </ProtectedRoute>
          } />
          
          <Route path="/configuracion" element={
            <ProtectedRoute>
              <ConfiguracionGlobal />
            </ProtectedRoute>
          } />

          {/* Módulo de Tickets Internos y Tareas */}
          <Route path="/tickets" element={
            <ProtectedRoute>
              <TicketsManager />
            </ProtectedRoute>
          } />

          {/* Affiliate Marketing Platform */}
          <Route path="/affiliate" element={<Navigate to="/affiliate/agendamiento" replace />} />
          <Route path="/affiliate/agendamiento" element={
            <ProtectedRoute allowedRoles={['admin', 'affiliate', 'affiliate.agendamiento', 'marketing']}>
              <AffiliateAgendamiento />
            </ProtectedRoute>
          } />
          <Route path="/affiliate/facturacion" element={
            <ProtectedRoute allowedRoles={['admin', 'affiliate', 'affiliate.facturacion', 'affiliate.agendamiento', 'marketing']}>
              <AffiliateFacturacion />
            </ProtectedRoute>
          } />
          <Route path="/affiliate/configuracion" element={
            <ProtectedRoute allowedRoles={['admin', 'affiliate', 'affiliate.configuracion', 'affiliate.agendamiento', 'marketing']}>
              <AffiliateConfiguracion />
            </ProtectedRoute>
          } />

          {/* Portal Médico Radiólogo */}
          <Route path="/radiologo" element={<Navigate to="/radiologo/agenda" replace />} />
          <Route path="/radiologo/agenda" element={
            <ProtectedRoute allowedRoles={['admin', 'radiologo', 'radiologo.agenda', 'medico']}>
              <RadiologoPortal />
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </Suspense>
        </AppProvider>
      </AuthProvider>
      <Toaster position="top-right" containerStyle={{ zIndex: 999999 }} />
    </BrowserRouter>
  );
}
